/**
 * Import/Export Logic
 *
 * Extracted from App.js to improve modularity and testability.
 * Handles YAML file import/export operations with validation.
 *
 * @module features/importExport
 */

import YAML from 'yaml';
import { validate } from '../validation';
import { removeStaleCameraReferences } from '../utils/cameraReferences';
import { withLegacyConverterKeys } from '../io/legacyCompat';
import {
  encodeYaml,
  downloadYamlFile,
  formatDeterministicFilename
} from '../io/yaml';
import { emptyFormData, genderAcronym } from '../valueList';

/** Every message for an import that fails says so: the page keeps the form it already has. */
const FORM_NOT_CHANGED = 'The form was not changed.';

/**
 * Import YAML files and prepare form data
 *
 * Parses YAML content, validates against schema and rules, and prepares
 * form data with appropriate defaults. Handles partial imports when some
 * fields have validation errors.
 *
 * @param {File} file - File object to import
 * @param {Object} [options] - Optional configuration
 * @param {Function} [options.onProgress] - Progress callback (not implemented yet)
 * @returns {Promise<Object>} Result object
 * @returns {boolean} result.success - Whether import succeeded
 * @returns {string|null} result.error - Error message if failed
 * @returns {Object|null} result.formData - Validated form data; null when nothing can be imported
 *   (no file, unreadable, rich text, not valid YAML, no metadata fields, or an unexpected error),
 *   so the caller keeps the form it already has
 * @returns {Object} [result.importSummary] - Import summary (only present on success)
 * @returns {number} result.importSummary.totalFields - Total fields in YAML file
 * @returns {string[]} result.importSummary.importedFields - Successfully imported field names
 * @returns {Array<{field: string, reason: string}>} result.importSummary.excludedFields - Excluded fields with validation reasons
 * @returns {boolean} result.importSummary.hasExclusions - Whether any fields were excluded
 *
 * @example
 * const result = await importFiles(file);
 * if (result.success) {
 *   actions.loadImportedFormData(result.formData);
 *   if (result.importSummary) {
 *     showImportSummary(result.importSummary);
 *   }
 * } else {
 *   alert(result.error);
 * }
 */
export async function importFiles(file, options = {}) {
  const { onProgress } = options;

  // Validate input
  if (!file) {
    return {
      success: false,
      error: 'No file provided',
      formData: null,
    };
  }

  // Call progress callback if provided
  if (onProgress) {
    onProgress({ stage: 'reading', progress: 0 });
  }

  // Read file content
  return new Promise((resolve) => {
    const reader = new FileReader();

    // A file that cannot be read leaves the form as it was: formData is null, and the page applies
    // formData only when it is set. (An empty form here would wipe what the user had loaded.)
    reader.onerror = () => {
      // eslint-disable-next-line no-alert
      window.alert(`Error reading file. Please try again.\n\n${FORM_NOT_CHANGED}`);
      resolve({
        success: false,
        error: 'Error reading file. Please try again.',
        formData: null,
      });
    };

    const handleLoad = (evt) => {
      if (onProgress) {
        onProgress({ stage: 'parsing', progress: 30 });
      }

      // TextEdit saves a new document as rich text unless it is made plain text first. The
      // parser's error for that says nothing useful, so say what to do instead.
      if (/^\s*\{\\rtf/.test(evt.target.result)) {
        // eslint-disable-next-line no-alert
        window.alert(
          'This file is saved as rich text (RTF), not plain text, so it cannot be read.\n\n' +
          'In TextEdit, choose Format > Make Plain Text, save the file, and upload it again.\n\n' +
          FORM_NOT_CHANGED
        );
        resolve({
          success: false,
          error: 'The file is rich text (RTF), not plain-text YAML.',
          formData: null,
        });
        return;
      }

      // Parse YAML with error handling
      let jsonFileContent;
      try {
        jsonFileContent = YAML.parse(evt.target.result);
      } catch (parseError) {
        // eslint-disable-next-line no-alert
        window.alert(
          `Invalid YAML file: ${parseError.message}\n\n` +
          `The file could not be parsed. Please check the YAML syntax and try again.\n\n` +
          FORM_NOT_CHANGED
        );
        resolve({
          success: false,
          error: `Invalid YAML file: ${parseError.message}`,
          formData: null,
        });
        return;
      }

      // An empty file, plain text, a list, or a mapping with none of the form's fields (`{}`, or
      // another tool's YAML picked by mistake) parses without error but holds no metadata.
      // Importing it would replace the form with an empty one.
      if (
        jsonFileContent === null ||
        typeof jsonFileContent !== 'object' ||
        Array.isArray(jsonFileContent) ||
        !Object.keys(emptyFormData).some((key) => Object.hasOwn(jsonFileContent, key))
      ) {
        // eslint-disable-next-line no-alert
        window.alert(
          'No metadata was found in this file, so nothing was imported.\n\n' +
          'The file is empty, or it is not a metadata YAML file (one with fields such as lab, ' +
          'session_id and subject).\n\n' +
          FORM_NOT_CHANGED
        );
        resolve({
          success: false,
          error: 'The file does not contain any metadata fields.',
          formData: null,
        });
        return;
      }

      if (onProgress) {
        onProgress({ stage: 'validating', progress: 50 });
      }

      // Files written before stale camera references were cleaned up may
      // reference cameras that no longer exist; drop those references rather
      // than excluding the whole section on validation.
      jsonFileContent = removeStaleCameraReferences(jsonFileContent);

      // Validate YAML content
      const issues = validate(jsonFileContent);

      if (issues.length === 0) {
        // No validation errors - ensure relevant keys exist and load all data
        const formContentKeys = Object.keys(emptyFormData);
        formContentKeys.forEach((key) => {
          if (!Object.hasOwn(jsonFileContent, key)) {
            jsonFileContent[key] = emptyFormData[key];
          }
        });

        if (onProgress) {
          onProgress({ stage: 'complete', progress: 100 });
        }

        // Build import summary for successful import
        const importedFields = formContentKeys.filter(key =>
          Object.hasOwn(jsonFileContent, key) &&
          jsonFileContent[key] !== emptyFormData[key]
        );

        resolve({
          success: true,
          error: null,
          formData: structuredClone(jsonFileContent),
          importSummary: {
            totalFields: formContentKeys.filter(key => Object.hasOwn(jsonFileContent, key)).length,
            importedFields,
            excludedFields: [],
            hasExclusions: false,
          },
        });
        return;
      }

      // Validation errors found - partial import
      if (onProgress) {
        onProgress({ stage: 'partial-import', progress: 70 });
      }

      // Extract top-level field IDs from paths (e.g., "cameras[0].id" → "cameras")
      const allErrorIds = [
        ...new Set(
          issues.map(issue => {
            const topLevelField = issue.path.split('[')[0].split('.')[0];
            return topLevelField;
          })
        )
      ];

      const formContent = structuredClone(emptyFormData);
      const formContentKeys = Object.keys(formContent);

      // Import only fields that don't have validation errors
      // and match the expected type
      formContentKeys.forEach((key) => {
        if (
          !allErrorIds.includes(key) &&
          Object.hasOwn(jsonFileContent, key)
        ) {
          // Check type compatibility before importing
          const expectedType = typeof formContent[key];
          const actualType = typeof jsonFileContent[key];

          // Only import if types match
          if (expectedType === actualType) {
            formContent[key] = structuredClone(jsonFileContent[key]);
          }
        }
      });

      // Ensure subject exists
      if (!formContent.subject) {
        formContent.subject = structuredClone(emptyFormData.subject);
      }

      // Validate and fix subject.sex
      const genders = genderAcronym();
      if (!genders.includes(formContent.subject.sex)) {
        formContent.subject.sex = 'U';
      }

      if (onProgress) {
        onProgress({ stage: 'complete', progress: 100 });
      }

      // Build import summary
      const importedFields = formContentKeys.filter(key =>
        !allErrorIds.includes(key) && Object.hasOwn(jsonFileContent, key)
      );

      const excludedFields = allErrorIds.map(fieldId => ({
        field: fieldId,
        reason: issues
          .filter(issue => issue.path.split('[')[0].split('.')[0] === fieldId)
          .map(issue => issue.message)[0] || 'Validation error'
      }));

      resolve({
        success: true,
        error: null,
        formData: structuredClone(formContent),
        importSummary: {
          totalFields: formContentKeys.filter(key => Object.hasOwn(jsonFileContent, key)).length,
          importedFields,
          excludedFields,
          hasExclusions: excludedFields.length > 0,
        },
      });
    };

    // An error nothing above expects must still settle the import with a message: uncaught, it
    // left the upload doing nothing at all.
    reader.onload = (evt) => {
      try {
        handleLoad(evt);
      } catch (error) {
        // eslint-disable-next-line no-alert
        window.alert(
          `The file could not be imported because of an unexpected error: ${error.message}\n\n` +
          FORM_NOT_CHANGED
        );
        resolve({
          success: false,
          error: `The file could not be imported: ${error.message}`,
          formData: null,
        });
      }
    };

    reader.readAsText(file, 'UTF-8');
  });
}

/**
 * Export form data as YAML file
 *
 * Validates form data, encodes as YAML, and triggers browser download.
 * Returns validation issues if validation fails instead of downloading.
 *
 * @param {Object} model - Form data to export
 * @param {Object} [options] - Optional configuration
 * @param {Function} [options.onProgress] - Progress callback (not implemented yet)
 * @returns {Object} Result object with success, error, validationIssues, yaml, and filename
 *
 * @example
 * const result = exportAll(formData);
 * if (result.success) {
 *   // Download triggered automatically
 *   console.log(`Downloaded: ${result.filename}`);
 * } else {
 *   displayErrors(result.validationIssues);
 * }
 */
export function exportAll(model, options = {}) {
  const { onProgress } = options;

  if (onProgress) {
    onProgress({ stage: 'validating', progress: 0 });
  }

  // Clone to avoid mutations
  const form = structuredClone(model);

  // Validate using unified validation API (schema + rules)
  const issues = validate(form);

  // If validation passes, generate and download YAML
  if (issues.length === 0) {
    if (onProgress) {
      onProgress({ stage: 'encoding', progress: 50 });
    }

    // Duplicate keys released trodes_to_nwb versions still read; validation
    // above ran on the canonical model.
    const yAMLForm = encodeYaml(withLegacyConverterKeys(form));
    const fileName = formatDeterministicFilename(form);

    if (onProgress) {
      onProgress({ stage: 'downloading', progress: 80 });
    }

    downloadYamlFile(fileName, yAMLForm);

    if (onProgress) {
      onProgress({ stage: 'complete', progress: 100 });
    }

    return {
      success: true,
      error: null,
      validationIssues: [],
      yaml: yAMLForm,
      filename: fileName,
    };
  }

  // Validation failed - return issues
  return {
    success: false,
    error: 'Validation failed',
    validationIssues: issues,
    yaml: null,
    filename: null,
  };
}
